import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HeatMapCell {
  value: string | number;
  color?: 'green' | 'amber' | 'red' | 'blue' | 'gray' | 'accent' | 'purple' | 'cyan';
}

interface HeatMapProps {
  title?: string;
  rowLabels: string[];
  columnLabels: string[];
  data: HeatMapCell[][]; // [row][col]
  showLegend?: boolean;
  legendItems?: Array<{ label: string; color: string }>;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Color mapping
// ---------------------------------------------------------------------------

const CELL_COLORS: Record<string, { bg: string; text: string }> = {
  green: { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-800' },
  red: { bg: 'bg-red-100', text: 'text-red-800' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
  accent: { bg: 'bg-accent-100', text: 'text-accent-800' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-800' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-800' },
};

const LEGEND_DOT_COLORS: Record<string, string> = {
  green: 'bg-emerald-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
  blue: 'bg-blue-400',
  gray: 'bg-gray-400',
  accent: 'bg-accent-400',
  purple: 'bg-purple-400',
  cyan: 'bg-cyan-400',
};

function getCellClasses(color?: string): string {
  if (color && CELL_COLORS[color]) {
    return `${CELL_COLORS[color].bg} ${CELL_COLORS[color].text}`;
  }
  return 'bg-white text-gray-700';
}

function getLegendDotClass(color: string): string {
  return LEGEND_DOT_COLORS[color] || 'bg-gray-400';
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HeatMap: React.FC<HeatMapProps> = ({
  title,
  rowLabels,
  columnLabels,
  data,
  showLegend = false,
  legendItems,
  compact = false,
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const cellPadding = compact ? 'px-2 py-1' : 'px-4 py-3';
  const cellText = compact ? 'text-xs' : 'text-sm';

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Scrollable table container */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Column headers */}
          <thead>
            <tr>
              {/* Empty top-left corner cell */}
              <th
                className={[
                  'border border-gray-200 bg-gray-50',
                  cellPadding,
                  'sticky left-0 z-10',
                ].join(' ')}
                aria-label="Row labels"
              />
              {columnLabels.map((col, colIndex) => (
                <th
                  key={colIndex}
                  className={[
                    'border border-gray-200 bg-gray-50',
                    cellPadding,
                    compact ? 'text-[10px]' : 'text-xs',
                    'font-semibold uppercase tracking-wider text-gray-600 text-center whitespace-nowrap',
                  ].join(' ')}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          {/* Data rows */}
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {/* Row label (sticky on mobile) */}
                <td
                  className={[
                    'border border-gray-200 bg-white',
                    cellPadding,
                    cellText,
                    'font-bold text-gray-900 whitespace-nowrap',
                    'sticky left-0 z-10',
                  ].join(' ')}
                >
                  {rowLabels[rowIndex] ?? ''}
                </td>

                {/* Data cells */}
                {row.map((cell, colIndex) => (
                  <td
                    key={colIndex}
                    className={[
                      'border border-gray-200',
                      cellPadding,
                      cellText,
                      'font-semibold text-center',
                      getCellClasses(cell.color),
                    ].join(' ')}
                  >
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      {showLegend && legendItems && legendItems.length > 0 && (
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {legendItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5"
              >
                <span
                  className={[
                    'inline-block w-3 h-3 rounded-full flex-shrink-0',
                    getLegendDotClass(item.color),
                  ].join(' ')}
                  aria-hidden="true"
                />
                <span className="text-xs text-gray-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeatMap;
